/**
 * iOS 기기 감지
 */
export const isIOS = (): boolean => {
  const userAgent = window.navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(userAgent);
};

/**
 * PWA 모드 감지
 */
export const isPWA = (): boolean => {
  // iOS Safari PWA 감지
  const nav = navigator as any;
  if ("standalone" in nav && nav.standalone) return true;

  // Android Chrome PWA 감지
  if (window.matchMedia("(display-mode: standalone)").matches) return true;
  if (window.matchMedia("(display-mode: fullscreen)").matches) return true;
  if (window.matchMedia("(display-mode: minimal-ui)").matches) return true;

  // URL 파라미터로 PWA 감지
  if (window.location.search.includes("standalone=true")) return true;

  return false;
};

/**
 * Service Worker 메시지 전송
 */
export const sendMessageToServiceWorker = (message: any): void => {
  if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage(message);
  }
};

/**
 * Service Worker 업데이트 확인
 */
export const checkServiceWorkerUpdate = async (): Promise<void> => {
  if ("serviceWorker" in navigator) {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration) {
      registration.update();
    }
  }
};

/**
 * 흰 화면 감지
 */
export const isBlankPage = (): boolean => {
  const body = document.body;
  const html = document.documentElement;

  // body와 html 모두 체크
  const hasBodyContent =
    body &&
    (body.children.length > 0 || (body.textContent?.trim().length || 0) > 0);

  const hasHtmlContent =
    html &&
    (html.children.length > 1 || // head + body 최소 2개
      (html.textContent?.trim().length || 0) > 0);
  return !hasBodyContent || !hasHtmlContent;
};

/**
 * PWA에서 강제 새로고침이 필요한지 확인
 */
export const shouldForceReload = (): boolean => {
  return isPWA() && isIOS();
};

/**
 * iOS PWA 흰화면 복구 시스템
 */
export const setupIOSPWARecovery = (): void => {
  if (!isIOS() || !isPWA()) return;

  let recoveryAttempts = 0;
  const maxRecoveryAttempts = 3;

  const performRecovery = () => {
    recoveryAttempts++;

    if (recoveryAttempts > maxRecoveryAttempts) {
      console.error("Max recovery attempts reached");
      return;
    }

    console.log(`iOS PWA Recovery attempt ${recoveryAttempts}`);

    // 캐시 클리어 후 새로고침
    if ("caches" in window) {
      caches
        .keys()
        .then((cacheNames: string[]) => {
          return Promise.all(
            cacheNames.map((cacheName: string) => caches.delete(cacheName))
          );
        })
        .then(() => {
          (window as any).location.reload();
        })
        .catch(() => {
          // 캐시 클리어 실패 시 단순 새로고침
          (window as any).location.reload();
        });
    } else {
      (window as any).location.reload();
    }
  };
  // 페이지 가시성 변경 감지
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) {
      // 포그라운드로 복귀 시 흰화면 체크
      setTimeout(() => {
        if (isBlankPage()) {
          performRecovery();
        }
      }, 500);
    }
  });

  // 앱 종료/재시작 감지
  window.addEventListener("pagehide", (event) => {
    if (event.persisted) {
      sessionStorage.setItem("ios_pwa_cached", Date.now().toString());
    }
  });

  window.addEventListener("pageshow", (event) => {
    const cachedTime = sessionStorage.getItem("ios_pwa_cached");
    if (event.persisted && cachedTime) {
      const timeDiff = Date.now() - parseInt(cachedTime);
      sessionStorage.removeItem("ios_pwa_cached");

      // 5분 이상 캐시된 경우 새로고침
      if (timeDiff > 5 * 60 * 1000) {
        performRecovery();
      }
    }
  });

  // 메모리 경고 감지 (iOS PWA 전용)
  window.addEventListener("beforeunload", () => {
    sessionStorage.setItem("ios_pwa_unload", Date.now().toString());
  });

  // 초기 로드 시 이전 비정상 종료 감지
  const unloadTime = sessionStorage.getItem("ios_pwa_unload");
  if (unloadTime) {
    const timeDiff = Date.now() - parseInt(unloadTime);
    sessionStorage.removeItem("ios_pwa_unload");

    // 1분 이내 재시작 시 흰화면일 가능성 높음
    if (timeDiff < 60 * 1000) {
      setTimeout(() => {
        if (isBlankPage()) {
          performRecovery();
        }
      }, 2000);
    }
  }
};

/**
 * iOS PWA 메모리 최적화
 */
export const optimizeIOSPWAMemory = (): void => {
  if (!isIOS() || !isPWA()) return;

  // 메모리 사용량 모니터링
  const checkMemoryUsage = () => {
    if ("memory" in performance) {
      const memory = (performance as any).memory;
      const usedMemory = memory.usedJSHeapSize / 1024 / 1024; // MB
      const memoryLimit = memory.jsHeapSizeLimit / 1024 / 1024; // MB

      // 메모리 사용률이 80% 이상이면 정리 수행
      if (usedMemory / memoryLimit > 0.8) {
        console.log("High memory usage detected, performing cleanup");

        // 가비지 컬렉션 강제 실행 (가능한 경우)
        if ("gc" in window) {
          (window as any).gc();
        }

        // 불필요한 이벤트 리스너 정리
        cleanupEventListeners();
      }
    }
  };

  // 주기적 메모리 체크
  setInterval(checkMemoryUsage, 30000); // 30초마다

  // 앱이 백그라운드로 갈 때 메모리 정리
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) {
      checkMemoryUsage();
    }
  });
};

/**
 * 불필요한 이벤트 리스너 정리
 */
const cleanupEventListeners = (): void => {
  // 여기에 정리할 이벤트 리스너들을 추가
  // 예: 스크롤 이벤트, 리사이즈 이벤트 등
};

/**
 * iOS PWA 전체 초기화 함수
 */
export const initializeIOSPWA = (): void => {
  if (!isIOS() || !isPWA()) return;

  console.log("Initializing iOS PWA optimizations");

  setupIOSPWARecovery();
  optimizeIOSPWAMemory();

  // 초기 흰화면 체크
  setTimeout(() => {
    if (isBlankPage()) {
      console.warn("Initial blank page detected, reloading");
      window.location.reload();
    }
  }, 3000);
};
